/**
 * Application entry point.
 *
 * Keeps the initial bundle small: WebXR and its Three.js integration are behind dynamic
 * imports inside `App`, so a device without immersive AR never downloads that code.
 */

import { App } from './app/App';

function mountFailure(container: HTMLElement, message: string): void {
  container.textContent = '';
  const panel = document.createElement('div');
  panel.className = 'fatal-error';
  panel.setAttribute('role', 'alert');
  const heading = document.createElement('h1');
  heading.textContent = 'Virtual Theremin could not start';
  const body = document.createElement('p');
  body.textContent = message;
  panel.append(heading, body);
  container.append(panel);
}

function bootstrap(): void {
  const container = document.getElementById('app');
  if (!container) {
    console.error('[theremin] #app container is missing from the document');
    return;
  }

  let app: App;
  try {
    app = new App({ container });
    app.start();
  } catch (error) {
    console.error('[theremin] fatal startup error', error);
    mountFailure(container, error instanceof Error ? error.message : String(error));
    return;
  }

  // Release the camera and audio device promptly when the page goes away. `pagehide` is
  // used rather than `unload` so it also fires on mobile Safari's back/forward cache path.
  globalThis.addEventListener('pagehide', (event: PageTransitionEvent) => {
    // A persisted page is going into the back/forward cache and may be restored, so keep
    // the app intact; a non-persisted pagehide is a real teardown.
    if (event.persisted) return;
    void app.dispose();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
